/**
 * FirstLoginChangePage — Forced Password Change (Screen 4).
 *
 * Shown when a user logs in for the first time with a temporary
 * password sent via WhatsApp. Requires setting a new password.
 *
 * Route: /change-password (redirected after first-login auth)
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

import { AuthLayout } from '../../components/auth/AuthLayout';
import { AuthCard } from '../../components/auth/AuthCard';
import { AuthInput } from '../../components/auth/AuthInput';
import { AuthButton } from '../../components/auth/AuthButton';
import {
  LockIcon,
  ShieldIcon,
  CheckIcon,
  EyeIcon,
  EyeOffIcon,
  AlertTriangleIcon,
} from '../../components/auth/AuthIcons';

export function FirstLoginChangePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const { showToast } = useToast();

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const isLongEnough = newPw.length >= 8;
  const passwordsMatch = newPw.length > 0 && newPw === confirmPw;
  const canSubmit = currentPw.length > 0 && isLongEnough && passwordsMatch && newPw !== currentPw;

  const newPwError =
    newPw.length > 0 && newPw.length < 8
      ? t('common:first_login.err_min8')
      : newPw.length >= 8 && currentPw.length > 0 && newPw === currentPw
      ? t('common:first_login.err_same')
      : '';

  // Guard: authenticated users without mustChangePassword go to their dashboard.
  if (user && !user.mustChangePassword) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post('/auth/change-password', { currentPassword: currentPw, newPassword: newPw });
      updateUser({ mustChangePassword: false });
      showToast('success', t('common:first_login.updated'));
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? t('common:first_login.failed');
      showToast('error', msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <AuthCard width={460}>
        {/* Header */}
        <div className="mb-6">
          {/* Action Required badge */}
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-3.5"
            style={{
              background: 'var(--amber-lt)',
              border: '1px solid #FDE68A',
            }}
          >
            <AlertTriangleIcon className="text-[var(--amber)]" />
            <span className="text-[11px] font-bold tracking-[0.06em] uppercase text-[var(--amber)]">
              {t('common:first_login.action_required')}
            </span>
          </div>

          <h1 className="font-display text-2xl font-extrabold tracking-[-0.03em] text-[var(--t1)] mb-1.5">
            {t('common:first_login.title')}
          </h1>
          <p className="text-sm font-medium text-[var(--t3)] leading-relaxed">
            {t('common:first_login.subtitle')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
          {/* Temporary password */}
          <AuthInput
            label={t('common:first_login.temp_password_label')}
            type={showCurrent ? 'text' : 'password'}
            placeholder={t('common:first_login.temp_password_ph')}
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            icon={<LockIcon />}
            rightIcon={showCurrent ? <EyeOffIcon /> : <EyeIcon />}
            onRightIconClick={() => setShowCurrent((p) => !p)}
            autoComplete="current-password"
          />

          {/* New password */}
          <AuthInput
            label={t('common:auth.new_password')}
            type={showNew ? 'text' : 'password'}
            placeholder={t('common:first_login.new_password_ph')}
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            icon={<ShieldIcon />}
            rightIcon={showNew ? <EyeOffIcon /> : <EyeIcon />}
            onRightIconClick={() => setShowNew((p) => !p)}
            autoComplete="new-password"
            error={newPwError}
          />

          {/* Confirm password */}
          <AuthInput
            label={t('common:auth.confirm_password')}
            type="password"
            placeholder={t('common:first_login.confirm_ph')}
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            icon={<CheckIcon />}
            error={confirmPw && !passwordsMatch ? t('common:form.passwords_mismatch') : ''}
            autoComplete="new-password"
          />

          <AuthButton
            type="submit"
            variant="primary"
            fullWidth
            loading={loading}
            disabled={!canSubmit}
            size="lg"
          >
            {t('common:first_login.submit')}
          </AuthButton>
        </form>
      </AuthCard>
    </AuthLayout>
  );
}

export default FirstLoginChangePage;
